/**
 * Dev tools - utility functions exposed on window in development mode
 */

import { GCP_AUTHUSER, GCP_PROJECT_ID, IS_DEV } from './constants'
import store from '~/store'
import api from '~/store/api'
import { selectProjectId } from '~/store/app/selectors'
import { selectPresets, selectCurrentPresetIndex } from '~/store/playback/selectors'
import { setCurrentPresetIndex } from '~/store/playback'
import { loadPresetSettings } from '~/store/playback/thunks'
import { captureVideoThumbnail } from '~/routes/mobile/cache/presetThumbnailCache'

const logger = console.scope('tools')

/**
 * Opens the imagine server logs for the current session in the cloud console. The cloud is detected from session.extraData: taskArn = AWS (CloudWatch), instanceName = GCP (Cloud Logging). See context/tools/gcp-logs.md.
 * @returns {string|null} console URL or null if no session
 */
function imaginelog() {
	const extra = window.session?.extraData
	if (extra?.instanceName) return gcpImagineLogsUrl(extra)
	if (extra?.taskArn) return awsImagineLogsUrl(extra.taskArn)
	console.error('No session infra data available (window.session.extraData)')
	return null
}

/**
 * GCP: Logs Explorer query for the session VM's container logs (cos_containers by instance_id)
 */
function gcpImagineLogsUrl(extra) {
	if (!extra.instanceId) {
		// instanceId is not guaranteed on the bulkInsert launch path — resolve it from the instances.insert audit log entry (see context/tools/gcp-logs.md, Step 1)
		console.error(`No extraData.instanceId for ${extra.instanceName} — resolve via the insert audit log`)
		return null
	}

	const filter = `logName="projects/${GCP_PROJECT_ID}/logs/cos_containers"\nresource.labels.instance_id="${extra.instanceId}"`
	const authuser = GCP_AUTHUSER ? `&authuser=${encodeURIComponent(GCP_AUTHUSER)}` : ''
	const url = `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(filter)}?project=${GCP_PROJECT_ID}${authuser}`

	window.open(url, '_blank')

	return url
}

/**
 * AWS: CloudWatch console URL for the session task's log stream
 */
function awsImagineLogsUrl(arn) {
	// Parse ARN: arn:aws:ecs:{region}:{account}:task/{cluster}/{taskId}
	const parts = arn.split(':')
	const region = parts[3]
	const resourceParts = parts[5].split('/')
	const cluster = resourceParts[1]
	const taskId = resourceParts[2]

	// Extract environment from cluster name (oylo-dev -> dev, oylo-prod -> prod)
	const env = cluster.replace('oylo-', '')

	// Build log group and stream
	const logGroup = `/ecs/oylo-ecs-task-${env}-imagine`
	const logStream = `ecs/imagine/${taskId}`

	// Encode for CloudWatch URL (/ -> $252F)
	const encode = str => str.replace(/\//g, '$252F')

	const url = `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/${encode(logGroup)}/log-events/${encode(logStream)}`

	window.open(url, '_blank')

	return url
}

/**
 * popthumbs - Populate missing preset thumbnails
 *
 * Utility to backfill thumbnails for presets created before the thumbnail feature. Iterates through presets, loads each one, waits for rendering, captures a thumbnail, and updates the preset via API.
 *
 * Usage: await popthumbs() in browser console while session is running
 */
async function popthumbs() {
	const state = store.getState()
	const presets = selectPresets(state)
	const projectId = selectProjectId(state)
	const originalIndex = selectCurrentPresetIndex(state)

	const missing = presets.filter(p => !p.thumbnailUrl)

	if (!missing.length) {
		logger.info('All presets have thumbnails')
		return
	}

	logger.info(`Generating thumbnails for ${missing.length} presets...`)

	for (let i = 0; i < missing.length; i++) {
		const preset = missing[i]
		const presetIndex = presets.findIndex(p => p.id === preset.id)

		logger.info(`[${i + 1}/${missing.length}] Processing preset: ${preset.name || preset.id}`)

		try {
			// Load preset settings
			store.dispatch(loadPresetSettings(preset.settings))
			store.dispatch(setCurrentPresetIndex(presetIndex))

			// Wait for server to apply settings
			await new Promise(r => setTimeout(r, 500))

			// Capture thumbnail
			const blob = await captureVideoThumbnail(window.output_video)
			if (!blob) {
				logger.warn(`  Failed to capture thumbnail`)
				continue
			}

			// Update preset with thumbnail
			await store.dispatch(
				api.endpoints.updatePreset.initiate({
					projectId,
					id: preset.id,
					thumbnail: blob,
				})
			)

			logger.info(`  Saved thumbnail (${Math.round(blob.size / 1024)}KB)`)
		} catch (err) {
			logger.error(`  Error: ${err.message}`)
		}
	}

	// Restore original preset
	if (originalIndex >= 0) {
		const originalPreset = presets[originalIndex]
		store.dispatch(loadPresetSettings(originalPreset.settings))
		store.dispatch(setCurrentPresetIndex(originalIndex))
	}

	logger.info('Done!')
}

// Expose to window in dev mode
if (IS_DEV) {
	window.imaginelog = imaginelog
	window.popthumbs = popthumbs
}
